import Box from '@mui/material/Box'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store'
import { ConstructionTab } from '../../../reducers/construction'
import { useCallback } from 'react'
import styled from '@emotion/styled'
import { Typography } from '@mui/material'
import { AIAnimatedIcon } from '../../Svgs'
import { BuildingElementStatus } from '../../../models/Construction'

interface TopPanelProps {}

const AnimatedBox = styled.div`
  display: flex;
  margin-top: 20px;
  border: 1px solid transparent;
  border-radius: 6px;
  background:
    linear-gradient(white, white) padding-box,
    linear-gradient(var(--angle), #1705e5, #3f69ff, #95baed, #ff3b47) border-box;
  backdrop-filter: blur(49.13999938964844px);
  box-shadow: 0px 2px 10px 0px #007aff80;
  animation: 3s rotate linear infinite;

  @keyframes rotate {
    to {
      --angle: 360deg;
    }
  }

  @property --angle {
    syntax: '<angle>';
    initial-value: 0deg;
    inherits: false;
  }
`

const ValidationAI = () => {
  const currentValidationMessages = useSelector(
    (state: RootState) => state.validation.currentValidationMessages,
  )

  if (!currentValidationMessages.length) {
    return null
  }

  return (
    <AnimatedBox>
      <Box sx={{ p: '10px', display: 'flex', gap: '10px' }}>
        <AIAnimatedIcon />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '10px',
          }}
        >
          {currentValidationMessages.map((message) => (
            <Typography
              key={message}
              sx={{
                fontWeight: 500,
                textAlign: 'start',
                color: '#06004A',
                fontSize: '14px',
                fontStyle: 'normal',
                lineHeight: 'normal',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {message}
            </Typography>
          ))}
        </Box>
      </Box>
    </AnimatedBox>
  )
}

const EditorAI = () => {
  const fetchingMessage = useSelector(
    (state: RootState) => state.construction.convertData.message,
  )
  const buildingElementsStatus = useSelector(
    (state: RootState) => state.construction.buildingElementStatus,
  )
  const runningElement = buildingElementsStatus.find(
    (element) => element.status === BuildingElementStatus.Running,
  )
  const buildingComplete = buildingElementsStatus.every(
    (element) => element.status === BuildingElementStatus.Complete,
  )

  const isInProgress =
    (!!fetchingMessage && !fetchingMessage.toLowerCase().startsWith('done')) ||
    !!runningElement

  return (
    <>
      {isInProgress && (
        <AnimatedBox>
          <Box sx={{ p: '10px', display: 'flex', gap: '10px' }}>
            <Box>
              <AIAnimatedIcon />
            </Box>
            <Box
              sx={{
                alignItems: 'flex-start',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
              }}
            >
              {!!fetchingMessage &&
                !fetchingMessage.toLowerCase().startsWith('done') && (
                  <Box sx={{ display: 'flex' }}>
                    <Typography
                      sx={{
                        color: '#04002E',
                        fontSize: '13px',
                        lineHeight: 'normal',
                        fontWeight: 500,
                      }}
                    >
                      {fetchingMessage}
                    </Typography>
                  </Box>
                )}
              {!!runningElement && (
                <Box sx={{ display: 'flex' }}>
                  <Typography
                    sx={{
                      color: '#04002E',
                      fontSize: '13px',
                      lineHeight: 'normal',
                      fontWeight: 500,
                    }}
                  >
                    {`Drawing ${runningElement.elementType.toString()}s...`}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </AnimatedBox>
      )}
    </>
  )
}

const TopPanel: React.FC<TopPanelProps> = () => {
  const selectedConstructionTab = useSelector(
    (state: RootState) => state.construction.selectedConstructionTab,
  )

  const renderTopPanelContent = useCallback(() => {
    switch (selectedConstructionTab) {
      case ConstructionTab.TakeOff:
        return null

      case ConstructionTab.Validation:
        return <ValidationAI />

      case ConstructionTab.Editor:
        return <EditorAI />
    }

    return null
  }, [selectedConstructionTab])

  return renderTopPanelContent()
}

export default TopPanel
